import { useEffect, useState } from 'react';

import { TabClickProps } from '@/components/atom/Tab';
import { BottomSheetTabsListItemProps } from '@/components/molecules/BottomSheet/BottomSheetTabs/BottomSheetTabsList';
import { OnClickQuestionProps } from '@/components/molecules/QuestionList/QuestionItem';
import { QnaRealtimeQueries } from '@/entities/qna/model/realtime';
import useAppDownloadCujModal from '@/hooks/Cuj/useAppDownloadCujModal';
import useGetQnaCategoryGroup from '@/hooks/qna/useGetQnaCategoryGroup';
import useCustomRouter from '@/hooks/useCustomRouter';
import { Nullable, ROUTES } from '@/types/common';
import { QnaCategoryGroup, QnaCategoryType } from '@/types/qna';
import Airbridge from '@/utils/airbridge';
import { communityLogger, getPageName } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';

interface QnaRealtimeViewModel {
  queries: QnaRealtimeQueries;
  onClickQuestion: ({ questionId, sectionName }: OnClickQuestionProps) => void;
  pageName: string;
  showCategoriesBottomSheet: boolean;
  selectedCategoriesBottomSheetTabItem: Nullable<BottomSheetTabsListItemProps>;
  toggleCategoriesBottomSheet: (value?: boolean) => void;
  onClickCategoriesBottomSheetTrigger: () => void;
  onClickCategoriesBottomSheetTabListItem: (item: BottomSheetTabsListItemProps) => void;
  categoryGroup?: QnaCategoryGroup;
  selectedTab: QnaCategoryType;
  onClickCategoriesBottomSheetTab: (tab: TabClickProps<QnaCategoryType>) => void;
  getSelectedTabItemList: () => BottomSheetTabsListItemProps[];
  onClickStickButton: () => void;
  bottomButtonText: string;
}

const BOTTOME_BUTTON_TEXT = '실시간 의료상담 물어보기';

const useQnaRealtimeViewModel = (): QnaRealtimeViewModel => {
  const { router, moveToPage } = useCustomRouter();
  const { page = '1', categoryId = '' } = router.query as QnaRealtimeQueries;

  const pageName = getPageName(router);

  const [showCategoriesBottomSheet, setShowCategoriesBottomSheet] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<QnaCategoryType>(QnaCategoryType.TREATMENT);
  const [selectedCategoriesBottomSheetTabItem, setSelectedCategoriesBottomSheetTabItem] =
    useState<Nullable<BottomSheetTabsListItemProps>>();

  const { categoryGroup } = useGetQnaCategoryGroup();

  // 카테고리 Bottom Sheet on/off
  const toggleCategoriesBottomSheet = (value = true) => {
    setShowCategoriesBottomSheet(value);
  };

  // 카테고리 Bottom Sheet on
  const onClickCategoriesBottomSheetTrigger = () => {
    communityLogger({
      eventName: 'filter_categories_click',
      pageName,
    });
    toggleCategoriesBottomSheet();
  };

  // 카테고리 Bottom Sheet Tab Click
  const onClickCategoriesBottomSheetTab = (tab: TabClickProps<QnaCategoryType>) => {
    setSelectedTab(tab.value);
  };

  // 카테고리 Bottom Sheet Tab Item Click
  const onClickCategoriesBottomSheetTabListItem = async (item: BottomSheetTabsListItemProps) => {
    communityLogger({
      eventName: 'category_item_click',
      pageName,
      payload: {
        categoryId: item.value,
      },
    });
    toggleCategoriesBottomSheet(false);
    setSelectedCategoriesBottomSheetTabItem(item);

    router.replace(
      {
        pathname: ROUTES.QNA.REALTIME.path,
        query: {
          categoryId: item.value,
          page: '1',
        },
      },
      undefined,
      { shallow: true }
    );
  };

  // 질문 클릭 -> 상세페이지 이동
  const onClickQuestion = ({ questionId, sectionName }: OnClickQuestionProps) => {
    moveToPage({
      webLink: `${ROUTES.QNA.DETAIL.path}/${questionId}`,
      mobileScreenName: ROUTES.QNA.DETAIL.appScreenName,
      params: { questionId },
    });

    communityLogger({
      eventName: 'quick_answer_item_click',
      pageName,
      payload: {
        section_name: sectionName ?? '',
        questionId,
      },
    });
  };

  // 하단 버튼 클릭
  const onClickStickButton = () => {
    if (isWebView()) {
      sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_PAGE, {
        screenName: ROUTES.QNA.WRITE.appScreenName,
      });
      return;
    }

    Airbridge.setDynamicLink({
      campaign: 'web_content_qna',
      content: 'web_bottom_flt_list',
      term: BOTTOME_BUTTON_TEXT,
    });
  };

  const getSelectedTabItemList = () => {
    if (!categoryGroup) return [] as BottomSheetTabsListItemProps[];
    return categoryGroup.tabsList.filter((tab) => tab.key === selectedTab)[0].categories;
  };

  // 글로벌 앱 설치 모달 트래킹 정보 저장
  const { setIsCujModalTrackingInfo } = useAppDownloadCujModal();

  useEffect(() => {
    setIsCujModalTrackingInfo({
      campaignName: 'web_content_qna',
      trackContent: 'web_mid_mdl_list',
    });
  }, []);

  useEffect(() => {
    if (categoryGroup && categoryId) {
      const selectedCategory = categoryGroup.categories.filter((category) => category.value === categoryId)[0];
      setSelectedCategoriesBottomSheetTabItem(selectedCategory);
    }
  }, [categoryGroup, categoryId]);

  useEffect(() => {
    if (!router.query.categoryId) {
      setSelectedCategoriesBottomSheetTabItem({} as BottomSheetTabsListItemProps);
    }
  }, [categoryId]);

  return {
    queries: {
      categoryId,
      page,
    },
    onClickQuestion,
    pageName,
    showCategoriesBottomSheet,
    selectedCategoriesBottomSheetTabItem,
    toggleCategoriesBottomSheet,
    onClickCategoriesBottomSheetTrigger,
    onClickCategoriesBottomSheetTabListItem,
    categoryGroup,
    selectedTab,
    onClickCategoriesBottomSheetTab,
    getSelectedTabItemList,
    onClickStickButton,
    bottomButtonText: BOTTOME_BUTTON_TEXT,
  };
};

export default useQnaRealtimeViewModel;
